import { ref, computed } from "vue";

const OVERLOCK_ASSIGNMENT_KEY = "overlock_assignment";

const generateMockUnits = () => [
  { id: "1", unitNumber: "0112" },
  { id: "2", unitNumber: "0119" },
  { id: "3", unitNumber: "0207" },
  { id: "4", unitNumber: "0301" },
  { id: "5", unitNumber: "0302" },
];

export function useMobileOverlock() {
  const units = ref([]);
  const selectedUnits = ref(new Set());
  const unitComments = ref(new Map());
  const completedUnits = ref([]);
  const loading = ref(false);
  const applyingOverlocks = ref(false);

  const loadUnits = async (facilityId) => {
    loading.value = true;
    try {
      units.value = generateMockUnits();

      loadSavedSelections();
    } catch (error) {
      console.error("Error loading units:", error);
      throw error;
    } finally {
      loading.value = false;
    }
  };

  const loadSavedSelections = () => {
    try {
      const saved = localStorage.getItem(OVERLOCK_ASSIGNMENT_KEY);
      if (saved) {
        const data = JSON.parse(saved);
        selectedUnits.value = new Set(data.selectedUnits || []);
        unitComments.value = new Map(data.unitComments || []);
      }
    } catch (error) {
      console.error("Error loading saved selections:", error);
    }
  };

  const saveSelections = () => {
    try {
      const data = {
        selectedUnits: Array.from(selectedUnits.value),
        unitComments: Array.from(unitComments.value.entries()),
        timestamp: new Date().toISOString(),
      };
      localStorage.setItem(OVERLOCK_ASSIGNMENT_KEY, JSON.stringify(data));
    } catch (error) {
      console.error("Error saving selections:", error);
    }
  };

  const isUnitSelected = (unitId) => {
    return selectedUnits.value.has(unitId);
  };

  const toggleUnit = (unitId, checked) => {
    if (checked) {
      selectedUnits.value.add(unitId);
    } else {
      selectedUnits.value.delete(unitId);
      unitComments.value.delete(unitId);
    }
    saveSelections();
  };

  const getUnitComment = (unitId) => {
    return unitComments.value.get(unitId) || "";
  };

  const updateComment = (unitId, comment) => {
    if (comment) {
      unitComments.value.set(unitId, comment);
    } else {
      unitComments.value.delete(unitId);
    }
    saveSelections();
  };

  const applyOverlocks = async () => {
    applyingOverlocks.value = true;
    try {
      await new Promise((resolve) => setTimeout(resolve, 500));

      const selectedUnitIds = Array.from(selectedUnits.value);
      completedUnits.value = units.value
        .filter((unit) => selectedUnitIds.includes(unit.id))
        .map((unit) => ({
          ...unit,
          comment: getUnitComment(unit.id),
          completedAt: new Date().toISOString(),
        }));

      saveCompletionRecord();
    } catch (error) {
      console.error("Error applying overlocks:", error);
      throw error;
    } finally {
      applyingOverlocks.value = false;
    }
  };

  const saveCompletionRecord = () => {
    try {
      const completionData = {
        completedUnits: completedUnits.value,
        completedAt: new Date().toISOString(),
        sessionId: generateSessionId(),
      };

      const historyKey = `overlock_completion_${Date.now()}`;
      localStorage.setItem(historyKey, JSON.stringify(completionData));

      selectedUnits.value.clear();
      unitComments.value.clear();
      localStorage.removeItem(OVERLOCK_ASSIGNMENT_KEY);
    } catch (error) {
      console.error("Error saving completion record:", error);
    }
  };

  const generateSessionId = () => {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  };

  const clearSession = () => {
    selectedUnits.value.clear();
    unitComments.value.clear();
    completedUnits.value = [];
    localStorage.removeItem(OVERLOCK_ASSIGNMENT_KEY);
  };

  const selectedUnitsArray = computed(() => {
    return Array.from(selectedUnits.value);
  });

  return {
    units,
    selectedUnits: selectedUnitsArray,
    completedUnits,
    loading,
    applyingOverlocks,
    loadUnits,
    isUnitSelected,
    toggleUnit,
    getUnitComment,
    updateComment,
    applyOverlocks,
    clearSession,
  };
}
